<template>
  <section-vue :padding-l-extra="true" class="business-benefits">
    <div class="business-benefits__container container grid-css">
      <h2 class="business-benefits__headline">
        {{ $t("headline") }}
      </h2>

      <div
        v-for="key in ['access', 'learning', 'insights']"
        :key="key"
        :class="`business-benefits__item business-benefits__item--${key}`"
      >
        <div class="business-benefits__item-copy">
          <h3 class="business-benefits__item-headline" v-html="$t(`items.${key}.headline`)" />

          <p
            class="business-benefits__item-text"
            v-html="
              $t(`items.${key}.text`, {
                totalBooks: $options.totalBooks,
                totalCategories: $options.totalCategories
              })
            "
          />
        </div>

        <pict class="business-benefits__item-image" :default="getPictureSource({ category: 'images', content: key })" />
        <pict
          class="business-benefits__item-feature"
          :default="getPictureSource({ category: 'features', content: key })"
        />
      </div>

      <div class="business-benefits__footer">
        <button-component
          class="business-benefits__cta"
          :use-router="true"
          :is-cta="true"
          href="book-a-demo"
          event-category="body-20210217-business_benefits"
          event-name="book-a-demo"
        >
          {{ $t("cta") }}
        </button-component>
      </div>
    </div>
  </section-vue>
</template>

<script>
import SectionVue from "@/shared/section"
import globals from "@/utils/globals"
import Pict from "@/shared/pict"
import buttonComponent from "@/shared/button"
import accessImageEn from "../images/benefits/access-image-en.png"
import accessImageEn2x from "../images/benefits/access-image-en@2x.jpg"
import accessImageDe from "../images/benefits/access-image-de.png"
import accessImageDe2x from "../images/benefits/access-image-de@2x.jpg"
import learningImage from "../images/benefits/learning-image.jpg"
import learningImage2x from "../images/benefits/learning-image@2x.jpg"
import insightsImage from "../images/benefits/insights-image.jpg"
import insightsImage2x from "../images/benefits/insights-image@2x.jpg"
import accessFeatureEn from "../images/benefits/access-feature-en.png"
import accessFeatureDe from "../images/benefits/access-feature-de.png"
import accessFeatureEn2x from "../images/benefits/access-feature-en@2x.png"
import accessFeatureDe2x from "../images/benefits/access-feature-de@2x.png"
import learningFeatureEn from "../images/benefits/learning-feature-en.png"
import learningFeatureEn2x from "../images/benefits/learning-feature-en@2x.png"
import learningFeatureDe from "../images/benefits/learning-feature-de.png"
import learningFeatureDe2x from "../images/benefits/learning-feature-de@2x.png"
import insightsFeature from "../images/benefits/insights-feature.png"
import insightsFeature2x from "../images/benefits/insights-feature@2x.png"

export default {
  name: "BusinessCompaniesLogos",
  components: {
    SectionVue,
    Pict,
    buttonComponent
  },
  totalBooks: globals.totalBooks,
  totalCategories: globals.totalCategories,
  images: {
    access: {
      en: [accessImageEn, accessImageEn2x],
      de: [accessImageDe, accessImageDe2x]
    },
    learning: {
      en: [learningImage, learningImage2x]
    },
    insights: {
      en: [insightsImage, insightsImage2x]
    }
  },
  features: {
    access: {
      en: [accessFeatureEn, accessFeatureEn2x],
      de: [accessFeatureDe, accessFeatureDe2x]
    },
    learning: {
      en: [learningFeatureEn, learningFeatureEn2x],
      de: [learningFeatureDe, learningFeatureDe2x]
    },
    insights: {
      en: [insightsFeature, insightsFeature2x]
    }
  },
  i18n: {
    messages: {
      en: {
        headline: "Make it easy to create a learning habit in your workplace with Blinkist",
        items: {
          access: {
            headline: "Fast access to ideas and inspiration for work and life",
            text: "Give your team instant access to key insights from <mark>{totalBooks}+ books</mark> and podcasts across <mark>{totalCategories} categories</mark>."
          },
          learning: {
            headline: "Learning that fits your team",
            text: "Help your team learn wherever and whenever they have <mark>15 minutes</mark> spare, with our <mark>mobile-first, audio-first app.</mark>"
          },
          insights: {
            headline: "Get detailed insights into your team’s learning",
            text: "See how your team learns, and what's important to them with your <mark>user analytics dashboard</mark>—so you can make data-led decisions."
          }
        },
        cta: "Get in touch"
      },
      de: {
        headline: "Mit Blinkist Business bringen Sie effiziente Allgemeinbildung in den Alltag Ihres Teams.",
        items: {
          access: {
            headline: "Zeitsparender Zugang zu Wissen",
            text: "Kernaussagen aus über <mark>{totalBooks} Sachbüchern</mark> aus <mark>{totalCategories} Themenkategorien</mark> wie Psychologie, Produktivität und Kommunikation"
          },
          learning: {
            headline: "Passend zum Alltag Ihres Teams",
            text: "Hochwertige Inhalte und praktische Funktionen sorgen für eine effiziente Integration in das Leben Ihres Teams."
          },
          insights: {
            headline: "Sie analysieren Ihren Bildungserfolg",
            text: "Eine Übersicht über das Lernverhalten Ihres Teams liefert Ihnen interessante Einblicke für datengetriebene Entscheidungen."
          }
        },
        cta: "Demo anfordern"
      }
    }
  },
  methods: {
    getPictureSource({ category, content }) {
      const defaultImages = this.$options[category][content]["en"]
      const localizedImages = this.$options[category][content][this.$i18n.locale]

      return {
        src: (localizedImages && localizedImages[0]) || defaultImages[0],
        srcset: {
          "2x": (localizedImages && localizedImages[1]) || defaultImages[1]
        }
      }
    }
  }
}
</script>

<style lang="scss">
.business-benefits {
  $root: &;

  &__headline {
    @include h2;
    @include grid-css-element(12);

    margin-bottom: $spacing-32;

    text-align: center;
  }

  &__item {
    @include grid-css-element(12);

    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: $spacing-48;
  }

  &__item-copy {
    margin-bottom: $spacing-16;

    text-align: center;

    mark {
      font-weight: bold;
    }
  }

  &__item-image {
    position: relative;
    z-index: 1;

    img {
      display: block;
      max-width: 100%;

      border-radius: $border-radius-4;
    }
  }

  &__item-feature {
    position: relative;
    z-index: 2;

    align-self: flex-end;
    margin-top: -5rem;

    grid-column-start: 4;

    img {
      display: block;
      max-width: 100%;

      border-radius: $border-radius-4;
      box-shadow: $spacing-8 $spacing-8 $midnight5;
    }
  }

  &__item-headline {
    @include font-style(h4, $color: $blue);

    margin-bottom: $spacing-24;
  }

  &__item-text {
    @include p0;

    margin: 0;
  }

  &__footer {
    @include grid-css-element(12);

    display: flex;
    justify-content: center;
  }

  #{&} &__cta {
    align-self: auto;
  }

  @include media(min-width, m) {
    &__headline {
      margin-bottom: $spacing-96;
    }

    &__item {
      @include grid-css(12);

      align-items: flex-start;
      margin-bottom: $spacing-96;

      &:nth-child(odd) {
        #{$root}__item-copy {
          order: 2;
        }

        #{$root}__item-image {
          order: 1;
          grid-column-start: 2;
        }

        #{$root}__item-feature {
          order: 3;
        }
      }

      &--learning {
        #{$root}__item-feature {
          @include grid-css-element(4, 5);
        }
      }
    }

    &__item-headline {
      @include font-style(h3, $color: $blue);
    }

    &__item-copy {
      @include grid-css-element(6);

      margin-bottom: $spacing-48;
      padding-top: $spacing-48;

      text-align: left;
    }

    &__item-image {
      @include grid-css-element(5);

      img {
        position: absolute;
      }
    }

    &__item-feature {
      @include grid-css-element(5, 4);

      margin-top: 0;

      img {
        width: 100%;
      }
    }
  }

  @include media(min-width, l) {
    &__item {
      @include grid-css(10);
      @include grid-css-element(10, 2);

      &--learning {
        #{$root}__item-feature {
          @include grid-css-element(3, 5);
        }
      }
    }

    &__headline {
      @include grid-css-element(10, 2);
    }

    &__item-copy {
      @include grid-css-element(5);
    }

    &__item-image {
      @include grid-css-element(4);
    }

    &__item-feature {
      @include grid-css-element(4, 4);
    }
  }
}
</style>
